// @ts-nocheck
// biome-ignore-all lint/correctness/useHookAtTopLevel: off

import { isEqual } from 'lodash';
import moment from 'moment-timezone';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { usePrevious, useQueryFetch } from 'sonata-react-components';

import {
  formatTimeFrame,
  getVariableFilterParsed as formatVariableFilter,
  getCartographyFilter
} from '@/helpers/reporting.helpers';
import { getLogo, getVariableValue } from '@/modules/reporting/reporting.resources';
import {
  getCartographyCountries,
  getCartographyLevel,
  getCartographyLevelIds,
  getCurrency,
  getTimeFrame,
  getTimeRange,
  getTimeZone,
  getVariableType,
  getVariableValues
} from '@/store/selectors/reportingAdvancedFilter.selectors';
import { getCartographyDictionary } from '@/ts-resources/reporting.resources';
import { FilterSection, Filters } from '@/ts-types/reporting.types';

type Cartography = {
  data: {
    countries: Record<string, any>[];
    items: Record<string, any>[];
    levels: Record<string, any>[];
  };
  error: string;
  status: 'idle' | 'pending' | 'success' | 'error';
};

type CartographyConfig = {
  cids?: string[];
  countries: string[];
  countryToken?: Record<string, any>;
  levelToken?: Record<string, any>;
};

type CartographyFilter = {
  country: string;
  end?: string;
  items: Record<string, any>;
  level: string;
  levelIds?: string[];
  start?: string;
  timezone?: string;
};

/**
 * Getting the items for the cartography filter
 *
 * @param {Object} config|filter - to config and filter the request
 * @param {Array} obj.config.countries - list of countries
 * @param {string} obj.config.countryToken - to cancel the country request
 * @param {string} obj.config.levelToken - to cancel the level request
 * @param {string} obj.filter.country -filtering of country responses
 * @param {string} obj.filter.level - getting current level
 * @param {string} obj.filter.levelIds - getting current level filtering by levelIds
 * @return {Array} [data, status, error]
 */
export const useCartography = ({
  config,
  filters
}: {
  config: CartographyConfig;
  filters: CartographyFilter;
}): [Cartography['data'], Cartography['status'], Cartography['error']] => {
  const [{ data, error, status }, setState] = useState<Cartography>({
    data: {
      countries: [],
      items: [],
      levels: []
    },
    error: '',
    status: 'idle'
  });

  const { cids, countries, countryToken, levelToken } = config;
  const { country, end, items: itemsConfig, level, start, timezone } = filters;

  const prevCountry = usePrevious(country);
  const prevLevel = usePrevious(level);

  const setPending = () => {
    setState(prevState => ({
      ...prevState,
      status: 'pending'
    }));
  };

  const setSuccess = ({ key, value }: { key: string; value: Record<string, any> }) => {
    setState(prevState => ({
      ...prevState,
      data: {
        ...prevState.data,
        [key]: value
      },
      status: 'success'
    }));
  };

  const setError = ({ message }: { message: string }) => {
    setState(prevState => ({
      ...prevState,
      error: message,
      status: 'error'
    }));
  };

  useEffect(() => {
    if (!isEqual(country, prevCountry)) {
      setState(prevState => ({
        ...prevState,
        data: {
          ...prevState.data,
          items: [],
          level: []
        }
      }));
    }
  }, [country]);

  useEffect(() => {
    if (!isEqual(level, prevLevel)) {
      setState(prevState => ({
        ...prevState,
        data: {
          ...prevState.data,
          items: []
        }
      }));
    }
  }, [country]);

  useEffect(() => {
    const getCountryData = async () => {
      try {
        setPending();
        const countriesResponse = await getCartographyDictionary(countries, countryToken);
        const countryOptions = countriesResponse
          .map((option: Record<string, any>) => ({
            id: option.id,
            label: option.name,
            tagsConfig: option.tagsConfig
          }))
          .sort((a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label));

        setSuccess({
          key: 'countries',
          value: countryOptions
        });
      } catch (e: any) {
        setError({ message: e.message });
      }
    };
    getCountryData();
  }, []);

  useEffect(() => {
    const getLevel = async () => {
      const { countries: countriesData } = data;
      try {
        setPending();
        const countryOption = countriesData.find(countryItem => countryItem.id === country[0].toUpperCase());
        const levelsOptions = countryOption
          ? countryOption.tagsConfig
              .filter((tag: Record<string, any>) => tag.reportingMapEventsEnable)
              .map((tag: Record<string, any>) => ({
                ...tag,
                country: countryOption.id,
                isDisabled: false,
                label: tag.name,
                value: tag.id
              }))
          : [];
        setSuccess({
          key: 'levels',
          value: levelsOptions
        });
      } catch (e: any) {
        setError({ message: e.message });
      }
    };

    if (country.length && data.countries.length) {
      getLevel();
    }
  }, [country, data.countries]);

  useEffect(() => {
    if (level) {
      const getLevelIds = async () => {
        try {
          setPending();
          const itemsResponse = await getVariableValue(
            `cartoPyramid.${level}`,
            cids,
            {
              cartography: {
                level0: [country[0].toUpperCase()],
                ...(itemsConfig.levelIds.length && {
                  [itemsConfig.level]: itemsConfig.levelIds
                })
              },
              end,
              start,
              timezone
            },
            levelToken
          );
          const itemsOptions = Object.entries(itemsResponse).map(([key, name]) => ({
            id: key,
            isDisabled: false,
            label: name,
            value: key
          }));
          itemsOptions.sort(({ label: option1Name }, { label: option2Name }) => {
            return (option1Name as string).localeCompare(option2Name as string);
          });
          setSuccess({
            key: 'items',
            value: itemsOptions
          });
        } catch (e: any) {
          setError({ message: e.message });
        }
      };
      getLevelIds();
    }
  }, [level]);

  return [data, status, error];
};

type ReportingFilterHookProps = {
  section: FilterSection;
};

export const useReportingFilters = ({ section }: ReportingFilterHookProps): Filters => {
  const getCartographyFilterParsed = (reportingSection: FilterSection): Record<string, string[]> | undefined => {
    // @ts-expect-error
    const generalCartographyCountries = useSelector(store => getCartographyCountries(store, 'general'));
    // @ts-expect-error
    const generalCartographyLevel = useSelector(store => getCartographyLevel(store, 'general'));
    // @ts-expect-error
    const generalCartographyLevelIds = useSelector(store => getCartographyLevelIds(store, 'general'));
    // @ts-expect-error
    const cartographyCountries = useSelector(store => getCartographyCountries(store, reportingSection));
    // @ts-expect-error
    const cartographyLevel = useSelector(store => getCartographyLevel(store, reportingSection));
    // @ts-expect-error
    const cartographyLevelIds = useSelector(store => getCartographyLevelIds(store, reportingSection));

    return getCartographyFilter(
      {
        cartographyCountries: generalCartographyCountries,
        cartographyLevel: generalCartographyLevel,
        cartographyLevelIds: generalCartographyLevelIds
      },
      {
        cartographyCountries,
        cartographyLevel,
        cartographyLevelIds
      }
    ) as Record<string, string[]> | undefined;
  };

  const getDateIntervalFilterParsed = (
    reportingSection: FilterSection
  ): { end: string; start: string; timezone: string } => {
    // @ts-expect-error
    let timeFrame = useSelector(store => getTimeFrame(store, 'general'));
    // @ts-expect-error
    let timezone = useSelector(store => getTimeZone(store, 'general'));

    if (reportingSection !== 'general') {
      // @ts-expect-error
      const sectionTimeFrame = useSelector(store => getTimeFrame(store, reportingSection));
      // @ts-expect-error
      const sectionTimezone = useSelector(store => getTimeZone(store, reportingSection));
      timeFrame = sectionTimeFrame || timeFrame;
      timezone = sectionTimezone || timezone;
    }
    const momentFormat = 'YYYY-MM-DDTHH:mm:ss';
    const normalizedTimeFrame: {
      end: string;
      start: string;
    } = {
      end: moment.utc(timeFrame.endDate).endOf('day').format(momentFormat),
      start: moment.utc(timeFrame.beginDate).startOf('day').format(momentFormat)
    };
    return {
      ...formatTimeFrame(normalizedTimeFrame, timezone),
      timezone
    };
  };

  const getVariableFilterParsed = (
    reportingSection: FilterSection
  ): Record<string, (string | number)[]> | undefined => {
    // @ts-expect-error
    let variableType = useSelector(store => getVariableType(store, 'general'));
    // @ts-expect-error
    let variableValues = useSelector(store => getVariableValues(store, 'general'));
    if (reportingSection !== 'general') {
      // @ts-expect-error
      const sectionVariableType = useSelector(store => getVariableType(store, reportingSection));
      // @ts-expect-error
      const sectionVariableValues = useSelector(store => getVariableValues(store, reportingSection));
      variableType = sectionVariableType || variableType;
      variableValues = sectionVariableValues || variableValues;
    }
    return variableType && variableValues.length > 0
      ? (formatVariableFilter(variableType, variableValues) as Record<string, (string | number)[]> | undefined)
      : undefined;
  };
  // @ts-expect-error
  const timeRange = useSelector(store => getTimeRange(store, 'general'));

  const cartographyFilter = getCartographyFilterParsed(section);
  const variableFilter = getVariableFilterParsed(section);

  return {
    // @ts-expect-error
    currency: useSelector(store => getCurrency(store, section)),
    dateInterval: getDateIntervalFilterParsed(section),
    filters: {
      ...(cartographyFilter && { cartography: cartographyFilter }),
      ...(variableFilter && { variable: variableFilter })
    },
    timeRange
  };
};

export const useCampaignsReportingLogo = (userId: string, campaignsIds: string[]) => {
  const baseImageURL = 'https://cdnsonata.taptapnetworks.com';

  const { data, isLoading } = useQueryFetch(['reportingLogo', userId, campaignsIds], async () =>
    getLogo(userId, campaignsIds)
  );

  return {
    isLoading,
    logo: data?.image && `${baseImageURL}${data.image}`
  };
};
